import React, { useEffect, useRef, useCallback, useContext } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Divider,
  Chip,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Link as MuiLink
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { resetInvoiceDetail } from "../../../store/slices/invoiceSlice";
import {
  useGetOrder,
  useCompleteOrder,
  useCancelOrder,
  useUpdateSalesOrderStatus
} from "../../../hooks/api/useSalesOrder";
import { useReportError } from "../../../hooks/useReportError";
import { useGetInvoiceByOrder } from "../../../hooks/api/useInvoice";
import { useListReceiptsByOrder } from "../../../hooks/api/useReceipt";
import OrderBarcodeScanner from "../common/OrderBarcodeScanner";
import BrandButton from "../../common/BrandButton/BrandButton";
import PrintButton from "../../common/PrintButton/PrintButton";
import PrintableContent from "../../common/PrintableContent/PrintableContent";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  PORTAL_PREFIX,
  PAGE_PORTAL_ORDERS_SALES_LIST,
  PAGE_PORTAL_ORDERS_INVOICE_DETAIL,
  PAGE_PORTAL_FINANCE_RECEIPT_DETAIL,
  KivuI18nContext,
  localizedPath,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import NoSalesOrderDetailFound from "./NoSalesOrderDetailFound";
import CommonLoader from "../../common/CommonLoader/CommonLoader";
import OpaqueIdOrText from "../../common/OpaqueIdOrText";
import DeleteSalesOrderModal from "./DeleteSalesOrderModal";
import EditSalesOrderModal from "./EditSalesOrderModal";
import AddOrderItemModal from "./AddOrderItemModal";
import EditOrderLineModal from "./EditOrderLineModal";
import RemoveOrderLineModal from "./RemoveOrderLineModal";
import UpdateOrderItemDiscountModal from "./UpdateOrderItemDiscountModal";
import type { OrderItem } from "../types/salesOrder";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PaymentIcon from "@mui/icons-material/Payment";
import {
  formatDateTime,
  formatOrderStatusLabel,
  getStatusColor,
  getLinkedInvoice,
  canIssueInvoice,
  canRecordPayment,
  canCancelOrder,
  canConfirmOrder,
  canCompleteOrder,
  isOrderEditable,
  orderItemLineLabel
} from "../utils/orderUtils";
import { useFormatCurrency } from "../../../hooks/useFormatCurrency";
import CreateInvoiceModal from "../../Invoice/CreateInvoiceModal";
import CreatePaymentModal from "../../Payment/CreatePaymentModal";
import { ORDER_LINE_ITEM_EDIT_STATUSES } from "../constants/barcodeScannerConstants";
import { OrderStatus } from "../enums/orderStatus";

const SalesOrderDetail: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const { t } = useKivunovaTranslation();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};

  const orderDetail = useSelector(
    (state: RootState) => state.salesOrders.salesOrderDetail
  );
  const invoiceDetail = useSelector(
    (state: RootState) => state.invoices.invoiceDetail
  );
  const receiptList = useSelector(
    (state: RootState) => state.receipts.receiptList
  );

  const dispatch = useDispatch();
  const reportError = useReportError();
  const formatCurrency = useFormatCurrency();
  const getOrder = useGetOrder();
  const completeOrder = useCompleteOrder();
  const cancelOrder = useCancelOrder();
  const updateSalesOrderStatus = useUpdateSalesOrderStatus();
  const getInvoiceByOrder = useGetInvoiceByOrder();
  const listReceiptsByOrder = useListReceiptsByOrder();

  const [openDelete, setOpenDelete] = React.useState(false);
  const [openEdit, setOpenEdit] = React.useState(false);
  const [openAddItem, setOpenAddItem] = React.useState(false);
  const [lineToEdit, setLineToEdit] = React.useState<OrderItem | null>(null);
  const [lineToDiscount, setLineToDiscount] = React.useState<OrderItem | null>(
    null
  );
  const [lineToRemove, setLineToRemove] = React.useState<OrderItem | null>(
    null
  );
  const [openCreateInvoice, setOpenCreateInvoice] = React.useState(false);
  const [openCreatePayment, setOpenCreatePayment] = React.useState(false);
  const printContentRef = useRef<HTMLDivElement>(null);
  const hasFetchedRef = useRef(false);

  const refetchOrder = useCallback(async () => {
    if (orderId) {
      try {
        await getOrder.mutateAsync({ orderId });
      } catch (err) {
        reportError("Failed to refetch sales order detail", err, {
          component: "SalesOrderDetail",
          orderId
        });
      }
    }
  }, [orderId, reportError]);

  useEffect(() => {
    if (orderId) {
      hasFetchedRef.current = true;
      (async () => {
        try {
          await getOrder.mutateAsync({ orderId });
        } catch (err) {
          reportError("Failed to fetch sales order detail on mount", err, {
            component: "SalesOrderDetail",
            orderId
          });
        }
      })();
    }
  }, [orderId, reportError]);

  useEffect(() => {
    if (!orderId) return;
    dispatch(resetInvoiceDetail());
    (async () => {
      try {
        await getInvoiceByOrder.mutateAsync({ orderId });
      } catch {
        // No invoice yet — expected
      }
    })();
  }, [orderId, dispatch]);

  useEffect(() => {
    if (orderId) {
      (async () => {
        try {
          await listReceiptsByOrder.mutateAsync({ orderId });
        } catch {
          // No receipts yet — expected
        }
      })();
    }
  }, [orderId]);

  if (getOrder.isPending || !hasFetchedRef.current) {
    return (
      <CommonLoader loadingMessage={t("loading-order", "Loading order...")} />
    );
  }

  if (!orderDetail || orderDetail.orderId !== orderId) {
    return <NoSalesOrderDetailFound />;
  }

  // Derived state — all computed from already-available local variables
  const linkedInvoice = getLinkedInvoice(invoiceDetail, orderId);
  const invoiceDetailPath = linkedInvoice
    ? localizedPath(
        `/${PORTAL_PREFIX}/${PAGE_PORTAL_ORDERS_INVOICE_DETAIL.replace(
          ":invoiceId",
          linkedInvoice.invoiceId
        )}`,
        language,
        defaultLang
      )
    : undefined;
  const orderReceipts = receiptList.filter((r) => r.orderId === orderId);
  const receiptDetailPath = (receiptId: string) =>
    localizedPath(
      `/${PORTAL_PREFIX}/${PAGE_PORTAL_FINANCE_RECEIPT_DETAIL.replace(
        ":receiptId",
        receiptId
      )}`,
      language,
      defaultLang
    );
  const issuable = canIssueInvoice(
    linkedInvoice,
    orderDetail.status,
    orderDetail
  );
  const payable = canRecordPayment(linkedInvoice);
  const canAddLineItems = ORDER_LINE_ITEM_EDIT_STATUSES.includes(
    orderDetail.status as OrderStatus
  );

  const salesListPath = localizedPath(
    `/${PORTAL_PREFIX}/${PAGE_PORTAL_ORDERS_SALES_LIST}`,
    language,
    defaultLang
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }} data-testid="sales-order-detail">
      {/* Header */}
      <Box
        sx={{
          mb: 2.5,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: { xs: 1, sm: 1.25 }
        }}
      >
        <MuiLink
          component={RouterLink}
          to={salesListPath}
          underline="hover"
          variant="body2"
          aria-label={t("back-to-sales-orders", "Back to Sales Orders")}
          sx={{
            display: { xs: "inline-flex", md: "none" },
            alignSelf: "flex-start",
            alignItems: "center",
            gap: 0.35,
            py: 0,
            mr: "auto",
            color: BRAND_COLOR,
            fontWeight: 500,
            fontSize: "0.8125rem",
            lineHeight: 1.2,
            textDecoration: "none",
            opacity: 0.92,
            transition: "opacity 0.15s ease, color 0.15s ease",
            "&:hover": {
              color: BRAND_HOVER,
              opacity: 1
            }
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 16, flexShrink: 0 }} />
          {t("back", "Back")}
        </MuiLink>

        <Box
          sx={{
            display: { xs: "grid", md: "flex" },
            gridTemplateColumns: "minmax(0, 1fr)",
            minWidth: 0,
            width: { xs: "100%", md: "100%" },
            maxWidth: {
              xs: "min(100%, 280px)",
              sm: "min(100%, 280px)",
              md: "none"
            },
            mx: { xs: "auto", md: 0 },
            gap: { xs: 1, sm: 1.25, md: 2 },
            justifyContent: { md: "flex-end" },
            alignItems: { md: "center" },
            flexWrap: { md: "wrap" },
            justifyItems: "stretch",
            "& > *": {
              width: { xs: "100%", md: "auto" },
              minHeight: { xs: 36, md: "auto" }
            }
          }}
        >
          {payable && (
            <BrandButton
              size="small"
              startIcon={<PaymentIcon />}
              onClick={() => setOpenCreatePayment(true)}
              data-testid="sales-order-btn-record-payment"
            >
              {t("record-payment", "Record Payment")}
            </BrandButton>
          )}

          {issuable && (
            <BrandButton
              size="small"
              startIcon={<ReceiptIcon />}
              onClick={() => setOpenCreateInvoice(true)}
              data-testid="sales-order-btn-issue-invoice"
            >
              {t("issue-invoice", "Issue Invoice")}
            </BrandButton>
          )}

          {canAddLineItems && (
            <BrandButton
              size="small"
              startIcon={<AddShoppingCartIcon />}
              onClick={() => setOpenAddItem(true)}
              data-testid="sales-order-btn-add-items"
            >
              {t("add-items", "Add items")}
            </BrandButton>
          )}

          {/* Edit order button — only visible when order is still editable */}
          {isOrderEditable(orderDetail.status) && (
            <BrandButton
              size="small"
              startIcon={<EditOutlinedIcon />}
              onClick={() => setOpenEdit(true)}
              data-testid="sales-order-btn-edit-order"
            >
              {t("edit-order", "Edit Order")}
            </BrandButton>
          )}

          <BrandButton
            size="small"
            variant="error"
            startIcon={<DeleteOutlineOutlinedIcon />}
            onClick={() => setOpenDelete(true)}
            data-testid="sales-order-btn-delete"
          >
            {t("delete", "Delete")}
          </BrandButton>

          <PrintButton
            size="small"
            targetRef={printContentRef}
            title={`${t("order", "Order")}: ${orderDetail.orderNumber}`}
            label={t("print-order", "Print Order")}
            data-testid="sales-order-btn-print-order"
          />
        </Box>
      </Box>

      {/* Printable Content */}
      <PrintableContent
        id="sales-order-printable-content"
        printTitle={`${t("order", "Order")}: ${orderDetail.orderNumber}`}
        printSubtitle={orderDetail.orderNumber}
        ref={printContentRef}
      >
        <OrderBarcodeScanner
          orderId={orderId ?? ""}
          orderStatus={orderDetail?.status ?? ""}
          onRefetch={refetchOrder}
          disabled={
            openDelete ||
            openEdit ||
            openAddItem ||
            openCreateInvoice ||
            openCreatePayment ||
            Boolean(lineToEdit) ||
            Boolean(lineToDiscount) ||
            Boolean(lineToRemove)
          }
        />

        {/* Order Info */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            {/* Fix: responsive font size so long order numbers don't overflow on small screens */}
            <Typography
              variant="h4"
              fontWeight="bold"
              data-testid="sales-order-detail-order-number"
              sx={{
                fontSize: { xs: "1.25rem", sm: "1.5rem", md: "2.125rem" },
                wordBreak: "break-word",
                mr: 1
              }}
            >
              {orderDetail.orderNumber}
            </Typography>
            <Chip
              label={formatOrderStatusLabel(orderDetail.status, t)}
              color={getStatusColor(orderDetail.status)}
              data-testid="sales-order-detail-status"
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Stack spacing={2}>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {t("customer", "Customer")}:
              </Typography>
              <OpaqueIdOrText value={orderDetail.customerId} />
            </Box>

            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {t("store", "Store")}:
              </Typography>
              <OpaqueIdOrText value={orderDetail.storeId} />
            </Box>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="body2" color="text.secondary">
                {t("invoice", "Invoice")}:
              </Typography>
              {linkedInvoice && invoiceDetailPath ? (
                <Typography
                  component={RouterLink}
                  to={invoiceDetailPath}
                  variant="body2"
                  fontWeight="medium"
                  data-testid="sales-order-link-view-invoice"
                  sx={{
                    color: BRAND_COLOR,
                    textDecoration: "underline",
                    textUnderlineOffset: 2,
                    textAlign: "right",
                    "&:hover": { color: BRAND_HOVER }
                  }}
                >
                  {t("view-invoice", "View Invoice")}
                </Typography>
              ) : (
                <Typography variant="body2" fontWeight="medium">
                  {t("not-available", "N/A")}
                </Typography>
              )}
            </Box>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
            >
              <Typography variant="body2" color="text.secondary">
                {t("receipts", "Receipts")}:
              </Typography>
              {orderReceipts.length > 0 ? (
                <Stack
                  spacing={0.5}
                  alignItems="flex-end"
                  sx={{ maxWidth: { xs: "65%", sm: "70%" } }}
                >
                  {orderReceipts.map((r, index) => {
                    const receiptLabel =
                      (r as { receiptNumber?: string }).receiptNumber ||
                      `${t("view-receipt", "View Receipt")} ${index + 1}`;
                    return (
                      <Typography
                        key={r.receiptId}
                        component={RouterLink}
                        to={receiptDetailPath(r.receiptId)}
                        variant="body2"
                        fontWeight="medium"
                        data-testid="sales-order-link-view-receipt"
                        sx={{
                          color: BRAND_COLOR,
                          textDecoration: "underline",
                          textUnderlineOffset: 2,
                          textAlign: "right",
                          "&:hover": { color: BRAND_HOVER }
                        }}
                      >
                        {receiptLabel}
                      </Typography>
                    );
                  })}
                </Stack>
              ) : (
                <Typography variant="body2" fontWeight="medium">
                  {t("not-available", "N/A")}
                </Typography>
              )}
            </Box>

            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {t("created", "Created")}:
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {formatDateTime(orderDetail.createdAt)}
              </Typography>
            </Box>

            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {t("updated", "Updated")}:
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {formatDateTime(orderDetail.updatedAt)}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* Order Items */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight="bold" mb={2}>
            {t("order-items", "Order Items")}
          </Typography>
          {orderDetail.items && orderDetail.items.length > 0 ? (
            <Box>
              {orderDetail.items.map((item, index) => (
                <Accordion
                  key={item.itemId}
                  defaultExpanded={index === 0}
                  sx={{
                    mb: 1,
                    "&:before": { display: "none" },
                    boxShadow: 1,
                    "&.Mui-expanded": { margin: "0 0 8px 0" }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      backgroundColor: "background.default",
                      "&:hover": { backgroundColor: "action.hover" }
                    }}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      width="100%"
                      pr={1}
                      gap={1}
                    >
                      <Typography variant="subtitle1" fontWeight="bold">
                        {orderItemLineLabel(item)}
                      </Typography>
                      <Box
                        display="flex"
                        gap={1}
                        alignItems="center"
                        flexShrink={0}
                      >
                        <Typography variant="body2" color="text.secondary">
                          {t("qty", "Qty")}: {item.quantity}
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {formatCurrency(item.lineTotal)}
                        </Typography>
                        {canAddLineItems && (
                          <Stack
                            direction="row"
                            spacing={0}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Tooltip title={t("edit-line", "Edit quantity")}>
                              <IconButton
                                size="small"
                                aria-label={t("edit-line", "Edit quantity")}
                                onClick={() => setLineToEdit(item)}
                                data-testid="sales-order-line-btn-edit-quantity"
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t("remove-line", "Remove line")}>
                              <IconButton
                                size="small"
                                color="error"
                                aria-label={t("remove-line", "Remove line")}
                                onClick={() => setLineToRemove(item)}
                                data-testid="sales-order-line-btn-remove"
                                sx={{
                                  "&:hover": {
                                    backgroundColor: "error.light",
                                    color: "error.dark"
                                  }
                                }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        )}
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={1}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          {t("item-id", "Item ID")}:
                        </Typography>
                        <Typography variant="body2">{item.itemId}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          {t("unit-price", "Unit Price")}:
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {formatCurrency(item.unitPrice)}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          {t(
                            "order-line-discount-per-unit-preview",
                            "Discount per unit"
                          )}
                          :
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {formatCurrency(item.discountPerUnit ?? 0)}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          {t(
                            "order-line-discount-total-preview",
                            "Discount total"
                          )}
                          :
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {formatCurrency(item.discountAmount ?? 0)}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          {t(
                            "order-line-discounted-unit-price",
                            "Discounted unit price"
                          )}
                          :
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {formatCurrency(
                            item.unitPrice - (item.discountPerUnit ?? 0)
                          )}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          {t("line-total", "Line Total")}:
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {formatCurrency(item.lineTotal)}
                        </Typography>
                      </Box>
                      {item.detail?.unit && (
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            {t("unit", "Unit")}:
                          </Typography>
                          <Typography variant="body2">
                            {item.detail.unit}
                          </Typography>
                        </Box>
                      )}
                      {canAddLineItems && (
                        <Box display="flex" justifyContent="flex-end" pt={1}>
                          <MuiLink
                            component="button"
                            type="button"
                            underline="hover"
                            onClick={() => setLineToDiscount(item)}
                            sx={{
                              border: "none",
                              background: "none",
                              p: 0,
                              cursor: "pointer",
                              color: BRAND_COLOR,
                              fontWeight: 500,
                              fontSize: "0.875rem",
                              "&:hover": { color: BRAND_HOVER }
                            }}
                          >
                            {(item.discountAmount ?? 0) > 0
                              ? t(
                                  "order-line-discount-edit-link",
                                  "Update discount"
                                )
                              : t(
                                  "order-line-discount-add-link",
                                  "Add discount"
                                )}
                          </MuiLink>
                        </Box>
                      )}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t("no-items", "No items on this order yet")}
            </Typography>
          )}
        </Paper>

        {/* Order Totals — moved below Order Items so the total reflects what's listed */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Stack spacing={2}>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {t("subtotal", "Subtotal")}:
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {formatCurrency(orderDetail.subtotalAmount)}
              </Typography>
            </Box>

            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {t("discount", "Discount")}:
              </Typography>
              <Typography
                variant="body2"
                fontWeight="medium"
                color="error.main"
              >
                {formatCurrency(orderDetail.discountAmount ?? 0)}
              </Typography>
            </Box>

            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {t("tax", "Tax")}:
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {formatCurrency(orderDetail.taxAmount)}
              </Typography>
            </Box>

            <Divider />

            <Box display="flex" justifyContent="space-between">
              <Typography variant="body1" fontWeight="bold">
                {t("total", "Total")}:
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {formatCurrency(orderDetail.totalAmount)}
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </PrintableContent>

      {/* Order Actions — Cancel, Confirm & Complete */}
      {(canCancelOrder(orderDetail.status) ||
        canConfirmOrder(orderDetail) ||
        canCompleteOrder(orderDetail.status)) && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            mt: 3,
            pt: 3,
            borderTop: 1,
            borderColor: "divider"
          }}
        >
          {canCancelOrder(orderDetail.status) && (
            <BrandButton
              variant="error"
              data-testid="sales-order-btn-cancel-order"
              onClick={async () => {
                try {
                  await cancelOrder.mutateAsync({
                    orderId: orderDetail.orderId
                  });
                  await refetchOrder();
                } catch (err) {
                  reportError(
                    "Failed to cancel sales order from detail page",
                    err,
                    {
                      component: "SalesOrderDetail",
                      orderId: orderDetail.orderId
                    }
                  );
                }
              }}
              disabled={cancelOrder.isPending}
            >
              {cancelOrder.isPending
                ? t("cancelling", "Cancelling…")
                : t("cancel-order", "Cancel Order")}
            </BrandButton>
          )}
          {canConfirmOrder(orderDetail) && (
            <BrandButton
              data-testid="sales-order-btn-confirm-order"
              onClick={async () => {
                try {
                  await updateSalesOrderStatus.mutateAsync({
                    orderId: orderDetail.orderId,
                    status: OrderStatus.OFFER_ACCEPTED
                  });
                  await refetchOrder();
                } catch (err) {
                  reportError(
                    "Failed to confirm sales order from detail page",
                    err,
                    {
                      component: "SalesOrderDetail",
                      orderId: orderDetail.orderId
                    }
                  );
                }
              }}
              disabled={
                updateSalesOrderStatus.isPending ||
                cancelOrder.isPending ||
                completeOrder.isPending
              }
            >
              {updateSalesOrderStatus.isPending
                ? t("confirming", "Confirming…")
                : t("confirm-order", "Confirm Order")}
            </BrandButton>
          )}
          {canCompleteOrder(orderDetail.status) && (
            <BrandButton
              data-testid="sales-order-btn-complete-order"
              onClick={async () => {
                try {
                  await completeOrder.mutateAsync({
                    orderId: orderDetail.orderId
                  });
                  await refetchOrder();
                } catch (err) {
                  reportError(
                    "Failed to complete sales order from detail page",
                    err,
                    {
                      component: "SalesOrderDetail",
                      orderId: orderDetail.orderId
                    }
                  );
                }
              }}
              disabled={completeOrder.isPending}
            >
              {completeOrder.isPending
                ? t("completing", "Completing…")
                : t("complete-order", "Complete Order")}
            </BrandButton>
          )}
        </Box>
      )}

      <AddOrderItemModal
        open={openAddItem}
        onClose={() => setOpenAddItem(false)}
        orderId={orderDetail.orderId}
        onAdded={refetchOrder}
      />

      <EditOrderLineModal
        open={Boolean(lineToEdit)}
        onClose={() => setLineToEdit(null)}
        orderId={orderDetail.orderId}
        line={lineToEdit}
        onUpdated={refetchOrder}
      />

      <UpdateOrderItemDiscountModal
        open={Boolean(lineToDiscount)}
        onClose={() => setLineToDiscount(null)}
        orderId={orderDetail.orderId}
        line={lineToDiscount}
        onUpdated={refetchOrder}
      />

      <RemoveOrderLineModal
        open={Boolean(lineToRemove)}
        onClose={() => setLineToRemove(null)}
        orderId={orderDetail.orderId}
        line={lineToRemove}
        onRemoved={refetchOrder}
      />

      <DeleteSalesOrderModal
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        salesOrder={orderDetail}
      />

      {/* Edit Order Modal */}
      <EditSalesOrderModal
        open={openEdit}
        onClose={() => {
          setOpenEdit(false);
          refetchOrder();
        }}
        salesOrder={orderDetail}
      />

      <CreateInvoiceModal
        open={openCreateInvoice}
        onClose={() => setOpenCreateInvoice(false)}
        orderId={orderDetail.orderId}
        orderStatus={orderDetail.status}
        order={orderDetail}
      />

      {linkedInvoice && (
        <CreatePaymentModal
          open={openCreatePayment}
          onClose={() => setOpenCreatePayment(false)}
          invoice={linkedInvoice}
          onPaymentSuccess={refetchOrder}
        />
      )}
    </Box>
  );
};

export default SalesOrderDetail;
